import { useOutliersConfigSlice } from 'widgets/outliers/store/config/configSlice';
import { useServices } from 'app/hooks';
import { useCallback, useContext, useEffect } from 'react';
import { useWidgetContext } from 'widgets/WidgetContext';
import { buildOutliersData } from 'widgets/outliers/data/outliersData';
import { FilteredPortfolioChangedEventType } from 'portfolio/store/actions';
import { useThrottledDataCalculation } from '@instinet-analytics/core-lib';
import { useSelectIsFeatureEnabled } from 'app/store/featureToggles/selectors';
import { WellKnownFeatureToggleKeys } from 'app/store/featureToggles/FeatureTogglesState';
import { OutliersPlType } from 'widgets/outliers/store/config/OutliersConfigState';
import AppContext from 'app/AppContext';
import { useLogger } from 'logging/writers/hooks';

export function useOutliersData(throttleSecs = 60) {
    const { portfolioService, portfolioEventService } = useServices();
    const { widgetKey } = useWidgetContext();
    const {
        config: { benchmarkType, plType: plTypeConfig, numberOfOrders },
    } = useOutliersConfigSlice();
    const isLastPriceEnabled = useSelectIsFeatureEnabled(
        WellKnownFeatureToggleKeys.LastPriceEnabled
    );
    const {
        currencyFormat: { exchangeRate },
    } = useContext(AppContext);
    const logger = useLogger('useOutliersData');

    const plType: OutliersPlType = isLastPriceEnabled
        ? plTypeConfig
        : 'realised';

    const deriveData = useCallback(() => {
        const orders = portfolioService.getFilteredOrders();

        return buildOutliersData(
            orders,
            benchmarkType,
            plType,
            numberOfOrders,

            exchangeRate ?? 1
        );
    }, [benchmarkType, plType, numberOfOrders, portfolioService, exchangeRate]);

    const { data, calculate } = useThrottledDataCalculation(
        `useOutliersData-${widgetKey}`,
        undefined,
        deriveData,
        logger,
        throttleSecs
    );

    useEffect(() => {
        const unsubscribe =
            portfolioEventService.subscribeFilteredPortfolioChanged((e) => {
                if (
                    e?.changedType === FilteredPortfolioChangedEventType.RESET
                ) {
                    calculate('Portfolio reset');
                }
            });
        return () => unsubscribe();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [portfolioEventService, widgetKey]);

    useEffect(() => {
        // When the config changes we immediately compute...
        calculate('config change');
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [benchmarkType, plType, numberOfOrders]);

    return data;
}
